import util from '../../../lib/util';
import store from '../../../store/store';
import i18n from "../../../i18n/i18n";
import Milestone from "../../../entities/milestone";
import {uuid} from "../../../@types/schema";
import Lead from '../../../entities/lead';

/**
 * Get all relationships for this id.
 * The id could belong to a contact, lead or matter.
 *
 * @param {uuid} entityId
 * @returns {Milestone[]}
 */
export function getMilestones(entityId: uuid): Milestone[] {
	/** @type {Milestone[]} */
	return store.getState().milestones
		.getMappingFor('entityId', entityId)
		.sort(util.sortByPosition);
}

/**
 * Class to handle evaluation of milestones.
 *
 * Note: this class is safe for use in worker threads!
 */
class MilestoneHelper {
	private milestones: Milestone[];

	constructor(milestones: Milestone[]) {
		this.milestones = milestones;
	}

	// Lazy getters: i18n is resolved at call time, not at class-definition
	// time. Eager `static X = i18n.ls(...)` initializers ran during this
	// module's evaluation; under the lead/milestone circular-import cycle
	// that could touch i18n before its module finished initializing, throwing
	// "Cannot access 'i18n' before initialization" (TDZ) when a route like
	// /config/cases/red-flags pulled MilestoneHelper in early.
	static get S_OPEN() { return i18n.ls('lead', 'open'); }
	static get S_AWAITING_INFO() { return i18n.ls('lead', 'awaitingInfo'); }
	static get S_CONTACT_INFO_RECEIVED() { return i18n.ls('lead', 'contactInfoReceived'); }
	static get S_CONTACT_MADE() { return i18n.ls('lead', 'contactMade'); }
	static get S_APPOINTMENT_MADE() { return i18n.ls('lead', 'appointmentMade'); }
	static get S_INITIAL_CONSULT() { return i18n.ls('lead', 'initialConsult'); }
	static get S_RETAINER_SIGNED() { return i18n.ls('lead', 'retainerSigned'); }
	static get S_RETAINER_FUNDED() { return i18n.ls('lead', 'retainerFunded'); }
	static get S_CONSULT_SUFFICIENT() { return i18n.ls('lead', 'consultSufficient'); }
	static get S_REFUSED() { return i18n.ls('lead', 'refused'); }
	static get S_LOST() { return i18n.ls('lead', 'lost'); }
	static get S_WAITING_ON() { return i18n.ls('lead', 'waitingOn'); }

	static get T_OPEN() { return i18n.d('lead', 'open'); }
	static get T_AWAITING_INFO() { return i18n.d('lead', 'awaitingInfo'); }
	static get T_CONTACT_INFO_RECEIVED() { return i18n.d('lead', 'contactInfoReceived'); }
	static get T_CONTACT_MADE() { return i18n.d('lead', 'contactMade'); }
	static get T_APPOINTMENT_MADE() { return i18n.d('lead', 'appointmentMade'); }
	static get T_INITIAL_CONSULT() { return i18n.d('lead', 'initialConsult'); }
	static get T_RETAINER_SIGNED() { return i18n.d('lead', 'retainerSigned'); }
	static get T_RETAINER_FUNDED() { return i18n.d('lead', 'retainerFunded'); }
	static get T_CONSULT_SUFFICIENT() { return i18n.d('lead', 'consultSufficient'); }
	static get T_REFUSED() { return i18n.d('lead', 'refused'); }
	static get T_LOST() { return i18n.d('lead', 'lost'); }
	static get T_WAITING_ON() { return i18n.d('lead', 'waitingOn'); }

	// Sales stages
	static N_OPEN = -1;
	static N_AWAITING_INFO = 0;
	static N_CONTACT_INFO_RECEIVED = 1;
	static N_CONTACT_MADE = 2;
	static N_APPOINTMENT_MADE = 3;
	static N_INITIAL_CONSULT = 4;
	static N_RETAINER_SIGNED = 5;
	static N_RETAINER_FUNDED = 6;

	// The following only apply if this is a paid consult
	static N_CONSULT_SUFFICIENT = 6.1; // We count that as a win

	static N_REFUSED = 7;
	static N_LOST = 8;
	static N_WAITING_ON = 9;

	// Memoized lazily — same TDZ reason as the S_/T_ getters above, and it
	// reads those getters, so it must not run at class-definition time.
	private static _positionMap: Map<number, string>;

	static get positionMap(): Map<number, string> {
		if (!MilestoneHelper._positionMap) {
			MilestoneHelper._positionMap = new Map<number, string>([
				[MilestoneHelper.N_AWAITING_INFO, MilestoneHelper.S_AWAITING_INFO],
				[MilestoneHelper.N_CONTACT_INFO_RECEIVED, MilestoneHelper.S_CONTACT_INFO_RECEIVED],
				[MilestoneHelper.N_CONTACT_MADE, MilestoneHelper.S_CONTACT_MADE],
				[MilestoneHelper.N_APPOINTMENT_MADE, MilestoneHelper.S_APPOINTMENT_MADE],
				[MilestoneHelper.N_INITIAL_CONSULT, MilestoneHelper.S_INITIAL_CONSULT],
				[MilestoneHelper.N_RETAINER_SIGNED, MilestoneHelper.S_RETAINER_SIGNED],
				[MilestoneHelper.N_RETAINER_FUNDED, MilestoneHelper.S_RETAINER_FUNDED],
				[MilestoneHelper.N_CONSULT_SUFFICIENT, MilestoneHelper.S_CONSULT_SUFFICIENT],
				[MilestoneHelper.N_REFUSED, MilestoneHelper.S_REFUSED],
				[MilestoneHelper.N_LOST, MilestoneHelper.S_LOST],
				[MilestoneHelper.N_WAITING_ON, MilestoneHelper.S_WAITING_ON]
			]);
		}
		return MilestoneHelper._positionMap;
	}

	/**
     * Get the stage name for a given position.
     *
     * @param {Milestone} ms
     * @returns {string}
	 */
	static getStage(ms: Milestone): string {
		if (!ms) throw new Error('No Milestone passed to getStage');
		return MilestoneHelper.positionMap.get(ms.position);
	}

	/**
	 * Get the date when the milestone was triggered.
	 *
	 * @param {Lead} lead
	 * @param {number} stage
	 * @returns {Date}
	 */
	static getMilestoneDate(lead: Lead, stage: number): Date {
		if (stage === this.N_OPEN) {
			// Look for the creation date
			return lead.createdOn;
		}

		return store.getState().milestones.getMappingFor('entityId', lead.id)[0]?.dateCompleted;
	}

	/**
	 * @param {Milestone} ms
	 * @param {boolean} [strict] - if strict, only RETAINER_FUNDED
	 * @returns {boolean}
	 */
	static isWon(ms: Milestone, strict?: boolean): boolean {
		if (!ms) return false;
		return strict ? ms.position === this.N_RETAINER_FUNDED
		              : (ms.position === this.N_RETAINER_FUNDED || ms.position === this.N_CONSULT_SUFFICIENT);
	}

	/**
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isRefusedLostOrParking(ms: Milestone): boolean {
		// Previously: `[this.N_REFUSED || this.N_LOST || this.N_WAITING_ON]`
		// — `||` short-circuits and the array was always `[7]` (N_REFUSED),
		// so this only matched REFUSED. The intent is "any of the three".
		return [this.N_REFUSED, this.N_LOST, this.N_WAITING_ON].includes(ms.position);
	}

	/**
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isConsultSufficient(ms: Milestone): boolean {
		return ms.position === this.N_CONSULT_SUFFICIENT;
	}

	/**
	 * Return true if won, lost, refused or consult sufficient.
	 *
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isWonOrLater(ms: Milestone): boolean {
		return ms.position >= this.N_RETAINER_FUNDED;
	}

	/**
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isLost(ms: Milestone): boolean {
		return Math.floor(ms.position) === this.N_LOST;
	}

	/**
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isWaitingOn(ms: Milestone): boolean {
		return Math.floor(ms.position) === this.N_WAITING_ON;
	}

	/**
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isPostWon(ms: Milestone): boolean {
		return ms.position > this.N_RETAINER_FUNDED && !this.isWon(ms);
	}

	/**
     * @param {Milestone} ms
     * @returns {boolean}
     */
	static isNormalPipeline(ms: Milestone): boolean {
		return Math.floor(ms.position) <= this.N_RETAINER_FUNDED;
	}

	/**
     * @param {Milestone} ms
     * @returns {boolean}
     */
	static isNotFinal(ms: Milestone): boolean {
		return ms.position <= this.N_RETAINER_SIGNED;
	}

	/**
	 * @param {Milestone} ms
	 * @returns {boolean}
	 */
	static isRefused(ms: Milestone): boolean {
		return Math.floor(ms.position) === this.N_REFUSED;
	}

	/**
	 * Get a list of milestone stage names.
	 *
	 * @param {number} position
	 * @returns {string|string[]}
	 */
	static getStageNames(position?: number): string | string[] {
		const stages = [
			// MilestoneHelper.AWAITING_INFO, // Not needed, this is not a "real" stage
			MilestoneHelper.S_CONTACT_INFO_RECEIVED,
			MilestoneHelper.S_CONTACT_MADE,
			MilestoneHelper.S_APPOINTMENT_MADE,
			MilestoneHelper.S_INITIAL_CONSULT,
			MilestoneHelper.S_CONSULT_SUFFICIENT,
			MilestoneHelper.S_RETAINER_SIGNED,
			MilestoneHelper.S_RETAINER_FUNDED,
			MilestoneHelper.S_REFUSED,
			MilestoneHelper.S_LOST,
			MilestoneHelper.S_WAITING_ON
		];

		if (position > 0) return MilestoneHelper.positionMap.get(position);
		else return stages;
	}

	/**
	 * Get a list of milestone stage names.
	 *
	 * @param {number} [position] - if provided, return the stage for that position
	 * @return {any|Map<any, any>}
	 */
	static getStages(position?: number): any | Map<number, string> {
		const stages = new Map<number, string>(); // Checked

		// stages.set(this.N_AWAITING_INFO, this.AWAITING_INFO); // Not needed, as this is not a "real" stage
		stages.set(this.N_CONTACT_INFO_RECEIVED, this.S_CONTACT_INFO_RECEIVED);
		stages.set(this.N_CONTACT_MADE, this.S_CONTACT_MADE);
		stages.set(this.N_APPOINTMENT_MADE, this.S_APPOINTMENT_MADE);
		stages.set(this.N_INITIAL_CONSULT, this.S_INITIAL_CONSULT);
		stages.set(this.N_CONSULT_SUFFICIENT, this.S_CONSULT_SUFFICIENT);
		stages.set(this.N_RETAINER_SIGNED, this.S_RETAINER_SIGNED);
		stages.set(this.N_RETAINER_FUNDED, this.S_RETAINER_FUNDED);
		stages.set(this.N_REFUSED, this.S_REFUSED);
		stages.set(this.N_LOST, this.S_LOST);
		stages.set(this.N_WAITING_ON, this.S_WAITING_ON);

		if (position > 0) {
			const result = stages.get(position);
			stages.clear();
			return result;
		}
		else return stages;
	}

	/**
	 * Just the stage numbers.
	 *
	 * @returns {number[]}
	 */
	static getStageNumbers(): number[] {
		return [1, 2, 3, 4, 5, 6, 6.1, 7, 8, 9];
	}

	/**
	 * Has the previous step been completed?
	 *
	 * @param {Milestone} ms
	 * @return {boolean}
	 */
	previousStepCompleted(ms: Milestone): boolean {
		// Need to find the milestone with a value about one whole number lower, then check than any of them are done.
		// For example, if we are passed a milestone with position 4, we need to check if any keys with a value
		// start with 3 are finished.
		const min = Math.floor(ms.position - 1);
		const max = ms.position;

		return this.milestones.filter(m => m.position >= min && m.position < max && !!m.dateCompleted).length > 0;
	}

	/**
	 * @param {number} position
	 * @return {Milestone}
	 */
	getStep(position: number): Milestone {
		return this.milestones.find(ms => ms.position === position);
	}

	/**
	 * @param {number} position
	 * @return {boolean}
	 */
	isStepCompleted(position: number): boolean {
		return !!(this.milestones.find(ms => ms.position === position)?.dateCompleted);
	}

	/**
	 * Have any of the won/lost/refused/waitingon been clicked on?
	 *
	 * @returns {boolean}
	 */
	hasPostWon(): boolean {
		return !!(this.milestones.find(ms => MilestoneHelper.isWonOrLater(ms) && !!ms.dateCompleted));
	}
}

export default MilestoneHelper;
